import React, {SyntheticEvent} from "react";
import {Dropdown, Form, Header} from "semantic-ui-react";

import "./view-manager.scss";
import {SidebarControlsConfiguration} from "../../../config/sidebar-configuration";
import {AppState} from "../../../stores/app-mobx";
import EsriLayerListViewModel from "@arcgis/core/widgets/LayerList/LayerListViewModel";
import {UiState} from "../../ui-state";
import {LayerList} from "../layer-list/layer-list";
import {autorun, runInAction} from "mobx";
import {observer} from "mobx-react";
import {TopicConfiguration} from "../../../config/topic-configuration";
import {MapState} from "../../map-state";
import {PathUtils} from "../../../utils/path-utils";
import {AppConfiguration} from "../../config/app-configuration";
import {MapViewerConfiguration} from "../../../config/map-viewer-configuration";
import {TopicSwitcherButton} from "../topic-switcher/topic-switcher-button";
import _ from "lodash";

export interface ViewManagerProps {
    controlsConfig: SidebarControlsConfiguration;
    appConfig: AppConfiguration;
    mapviewerConfig: MapViewerConfiguration;
    appStore: AppState;
    mapStore: MapState;
    small?: boolean;
    view: __esri.MapView | __esri.SceneView | undefined;
    onLoad?: (layerListViewModel: EsriLayerListViewModel) => any;
    uiStore: UiState;
}
@observer
export class ViewManager extends React.Component<ViewManagerProps, any> {
    autorunDisposers: any[] = [];
    widgetKey = "view-manager";
    constructor(props: ViewManagerProps) {
        super(props);
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
        }
        if(this.props.controlsConfig.topicSwitcher && this.props.mapviewerConfig.subtopics) {
            let defaultView: string | undefined;
            runInAction(()=> {
                if (props.appStore.subtopic && this.props.mapviewerConfig.subtopics?.some(
                    (v) => v.id === props.appStore.subtopic?.id)) {
                    defaultView = props.appStore.subtopic.id;
                } else {
                    defaultView = this.props.controlsConfig.topicSwitcher?.default;
                    if(!defaultView) {
                        console.warn("Default Subtopic nicht konfiguriert! (topicSwitcher.default)");
                    }
                    const currentView = this.props.mapviewerConfig.subtopics?.find((v) => v.id===defaultView);
                    if(!currentView) {
                        console.warn(`Default Subtopic "${defaultView}" nicht gefunden!`);
                    }
                }
            });
            this.state = {
                value: defaultView,
                revision: "initial"
            };
        }
        this.handleChange = this.handleChange.bind(this);
    }
    updateView(value?: string): void {
        runInAction(()=> {
            const v = this.props.mapviewerConfig.subtopics?.find(
                (view) => (view.id === value));
            if (v && value !== this.props.appStore.subtopic?.id) {
                this.props.appStore.setSubtopic(v);
            }
        });
    };
    handleChange(event: SyntheticEvent, data: any): void {
        const {mapviewerConfig} = this.props;
        this.setState({ value: data.value });
        this.updateView(data.value);
        const currentView = mapviewerConfig.subtopics?.find((v) => v.id===data.value);
        runInAction(()=> {
            this.props.appStore.setRoutePath(
                PathUtils.joinPath(this.props.appStore.topic?.routes[0], currentView?.routes[0]));
        });
    };
    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            const {uiStore} = this.props;
            if (uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }
            uiStore.addWidget(this.widgetKey, this);
            this.updateView(this.state.value);
        }));
        // Auf geändertes Subtopic reagieren.
        this.autorunDisposers.push(autorun(() => {
            const {appStore} = this.props;
            if(appStore.subtopic?.id!==this.state.value || appStore.subtopic?.revision!==this.state.revision){
                this.setState({ value: appStore.subtopic?.id, revision: appStore.subtopic?.revision });
                this.updateView(appStore.subtopic?.id);
            }
        }));
    }
    componentWillUnmount(): void {
        const {uiStore} = this.props;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        uiStore.removeWidget(this.widgetKey);
    }
    render(): JSX.Element | null {
        const {appConfig, appStore, controlsConfig, mapStore, mapviewerConfig, small, view, uiStore} = this.props;
        const {value} = this.state;
        let content;
        if(mapviewerConfig.subtopics?.length>1) {
            if (mapviewerConfig.topicSwitcher?.visible?.includes("toc")) {
                content = <TopicSwitcherButton
                    appStore={appStore}
                    mapStore={mapStore}
                    uiStore={uiStore}
                    topics={mapviewerConfig.subtopics}
                    mapviewerConfig={mapviewerConfig}></TopicSwitcherButton>;
            } else if (mapviewerConfig.topicSwitcher?.visible?.includes("view-manager")) {
                const options = mapviewerConfig.subtopics?.map(
                    (v: TopicConfiguration) => ({text: (v.label) ? v.label : v.name, value: v.id}));
                content = (
                    <div>
                        <Header as='h3'>{controlsConfig.topicSwitcher?.button}</Header>
                        <Form size={"small"}>
                            <Dropdown selection fluid options={options} value={value} onChange={this.handleChange}/>
                        </Form>
                    </div>
                );
            }
            return (
                <div className={"geo-view-manager"}>
                    <div className={"geo-view-manager-content"}>
                        {content}
                    </div>
                    <LayerList
                        appConfig={appConfig}
                        appStore={appStore}
                        controlsConfig={controlsConfig}
                        config={controlsConfig.layerList}
                        mapStore={mapStore}
                        small={!!small}
                        view={view}
                        uiStore={uiStore} />
                </div>
            );
        }else{
            return (
                <LayerList
                    appConfig={appConfig}
                    appStore={appStore}
                    controlsConfig={controlsConfig}
                    config={controlsConfig.layerList}
                    mapStore={mapStore}
                    small={!!small}
                    view={view}
                    uiStore={uiStore} />
            );
        }
    }
}
